import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Settings, Play, MoreHorizontal, User, Activity, Building2, Trash2, Copy, Pencil, LogOut, Brain, Database, Globe, AlertCircle, FolderPlus, Shield, Wrench, ChevronRight, Users, Crown, Search, UserPlus, X, Loader2, Download, Upload, Package, Zap, FileText, Cpu } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Badge } from "@/shared/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { Switch } from "@/shared/components/ui/switch"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import { useProjectStore } from "@/modules/agent/store/projectStore"
import type { AgentProject, ProjectSettings, ProjectMember } from "@/modules/agent/types/project"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { authService } from "@/shared/services/auth"
import type { User as SystemUser } from "@/shared/types/user"
import { useAuthStore } from "@/shared/store/authStore"
import { ProjectStatus } from "@/shared/types/enums"
import { ProjectApiService } from "@/modules/agent/services/project-api"
import { FlowApiService } from "@/modules/agent/services/flow-api"
import { useToast } from "@/shared/hooks/useToast"
import { useTools } from "@/modules/agent/hooks/useTools"
import { Node, Edge } from "reactflow"
import { ProjectPublishApiService } from "@/modules/app/services/app-api"
import { QuickPublishToAppDialog } from "@/modules/app"

// 默认节点和边（新项目默认）
const getDefaultNodes = (): Node[] => [
  {
    id: "entry",
    type: "entry",
    position: { x: 50, y: 200 },
    data: { label: "用户输入" },
  },
  {
    id: "output",
    type: "output",
    position: { x: 500, y: 200 },
    data: { label: "回复用户" },
  },
]

const getDefaultEdges = (): Edge[] => []

export default function Dashboard() {
  const navigate = useNavigate()
  const { projects, createProject: _createProject, updateProject, deleteProject, getAgentCount, addProject } = useProjectStore()
  const { user, logout } = useAuthStore()
  const { showError, showSuccess } = useToast()
  
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)
  const [projectToEdit, setProjectToEdit] = useState<AgentProject | null>(null)
  const [projectToDelete, setProjectToDelete] = useState<AgentProject | null>(null)
  const [projectToSettings, setProjectToSettings] = useState<AgentProject | null>(null)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [projectToDuplicate, setProjectToDuplicate] = useState<AgentProject | null>(null)
  const [duplicateProjectData, setDuplicateProjectData] = useState({ name: "", copyMembers: true })
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [newProject, setNewProject] = useState({
    company: "",
    name: "",
    description: "",
  })
  const [editProject, setEditProject] = useState({
    company: "",
    name: "",
    description: "",
  })
  
  // 项目设置相关状态
  const [projectSettings, setProjectSettings] = useState<ProjectSettings>({
    memory_enabled: false,
    user_context_api: '',
    enabled_tool_ids: [],
  })
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [isEditSaving, setIsEditSaving] = useState(false)
  
  // 获取所有系统工具（用于项目设置中选择可用工具）
  const { tools: allTools, builtinTools: _allBuiltinTools, isLoading: isLoadingTools } = useTools()
  
  // 项目导入/导出相关状态（v5.3 新增）
  const [isExporting, setIsExporting] = useState<string | null>(null) // 正在导出的项目 ID
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // 成员管理相关状态
  const [isMemberDialogOpen, setIsMemberDialogOpen] = useState(false)
  const [projectToManageMembers, setProjectToManageMembers] = useState<AgentProject | null>(null)
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)
  const [memberSearchKeyword, setMemberSearchKeyword] = useState('')
  const [searchedUsers, setSearchedUsers] = useState<SystemUser[]>([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [selectedUserToAdd, setSelectedUserToAdd] = useState<SystemUser | null>(null)
  const [selectedPermission, setSelectedPermission] = useState<'admin' | 'member'>('member')
  const [isAddingMember, setIsAddingMember] = useState(false)
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false)
  const [transferTargetUser, setTransferTargetUser] = useState<ProjectMember | null>(null)
  const [isTransferring, setIsTransferring] = useState(false)
  
  // 删除项目二次确认输入
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')

  // 删除成员确认弹窗状态
  const [isRemoveMemberDialogOpen, setIsRemoveMemberDialogOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null)
  
  // 搜索和公司分组筛选
  const [searchQuery, setSearchQuery] = useState('')
  const [companyFilter, setCompanyFilter] = useState<string>('all')
  const [groupByCompany, setGroupByCompany] = useState(false)

  // v11.2.1: App 发布状态 Map (projectId -> published_apps)
  const [projectAppStatus, setProjectAppStatus] = useState<Map<string, { app_id: string; app_name: string }[]>>(new Map())
  
  // v11.2.1: 发布到 App 弹窗状态
  const [isPublishToAppDialogOpen, setIsPublishToAppDialogOpen] = useState(false)
  const [projectToPublishToApp, setProjectToPublishToApp] = useState<AgentProject | null>(null)
  const [isRemovingMember, setIsRemovingMember] = useState(false)

  // 编辑项目信息并保存到后端
  const handleEditProject = useCallback(async () => {
    if (!projectToEdit || !editProject.company?.trim() || !editProject.name?.trim()) {
      return
    }

    setIsEditSaving(true)
    try {
      const response = await ProjectApiService.updateProject(projectToEdit.id, {
        company: editProject.company,
        name: editProject.name,
        description: editProject.description || "暂无描述",
      })

      if (response.success) {
        // 更新本地 store
        updateProject(projectToEdit.id, {
          company: editProject.company,
          name: editProject.name,
          description: editProject.description || "暂无描述",
        })
        showSuccess('项目信息已保存')
        setIsEditDialogOpen(false)
        setProjectToEdit(null)
      } else {
        showError('保存失败: 未知错误')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 保存项目信息失败:', error)
      showError('保存项目信息失败，请重试')
    } finally {
      setIsEditSaving(false)
    }
  }, [projectToEdit, editProject, updateProject, showSuccess, showError])

  // 导出项目（v5.3 新增）
  const handleExportProject = useCallback(async (project: AgentProject) => {
    setIsExporting(project.id)
    try {
      const response = await ProjectApiService.exportProject(project.id)
      if (response.success && response.data) {
        // 下载 JSON 文件
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${response.data.project.name}-export.json`
        a.click()
        URL.revokeObjectURL(url)
        showSuccess('项目导出成功')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 导出项目失败:', error)
      showError('导出失败，请稍后重试')
    } finally {
      setIsExporting(null)
    }
  }, [showSuccess, showError])

  // 导入项目（v5.3 新增）
  const handleImportProject = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    setIsImporting(true)
    try {
      const text = await file.text()
      const config = JSON.parse(text)
      
      const response = await ProjectApiService.importProject({ config })
      if (response.success && response.data) {
        showSuccess('导入成功！请配置工具和模型后发布')
        // 刷新项目列表
        const projectsResponse = await ProjectApiService.getProjects()
        if (projectsResponse.success && projectsResponse.data) {
          projectsResponse.data.forEach(p => addProject(p))
        }
        // 跳转到新项目
        navigate(`/project/${response.data.project_id}`)
      }
    } catch (error) {
      console.error('❌ [Dashboard] 导入项目失败:', error)
      showError('导入失败，请检查文件格式')
    } finally {
      setIsImporting(false)
      // 重置 input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [showSuccess, showError, addProject, navigate])

  // 从后端加载项目列表
  useEffect(() => {
    const loadProjects = async () => {
      setIsLoading(true)
      try {
        const response = await ProjectApiService.getProjects()
        if (response.success && response.data) {
          console.log(`✅ [Dashboard] 从后端加载了 ${response.data.length} 个项目:`)
          response.data.forEach((p, index) => {
            console.log(`  ${index + 1}. ID: ${p.id}, 名称: ${p.name}, 公司: ${p.company}`)
          })
          console.log(`📊 [Dashboard] 本地store中当前有 ${projects.length} 个项目:`)
          projects.forEach((p, index) => {
            console.log(`  ${index + 1}. ID: ${p.id}, 名称: ${p.name}, 公司: ${p.company}`)
          })
          
          // 将后端返回的项目同步到store
          // 注意：这里需要更新store中的所有项目，而不是追加
          // 由于store没有批量更新方法，我们需要逐个更新或替换
          response.data.forEach(backendProject => {
            const existingProject = projects.find(p => p.id === backendProject.id)
            if (existingProject) {
              // 更新已存在的项目（保留本地nodes和edges）
              updateProject(backendProject.id, {
                name: backendProject.name,
                company: backendProject.company,
                description: backendProject.description,
                status: backendProject.status,
                api_key: backendProject.api_key,
                api_key_created_at: backendProject.api_key_created_at,
                owner_id: backendProject.owner_id,
                current_version: backendProject.current_version,
                created_at: backendProject.created_at,
                updated_at: backendProject.updated_at,
              })
            } else {
              // 添加新项目（从后端获取的项目可能没有nodes和edges，需要设置默认值）
              const projectNodes = backendProject.nodes || []
              const projectEdges = backendProject.edges || []
              const hasFlowConfig = projectNodes.length > 0 || projectEdges.length > 0
              
              addProject({
                ...backendProject,
                nodes: hasFlowConfig ? projectNodes : getDefaultNodes(),
                edges: hasFlowConfig ? projectEdges : getDefaultEdges(),
              })
            }
          })
          
          // 移除后端不存在的项目（只保留后端返回的项目）
          // 注意：需要在同步完成后检查，因为projects变量可能还是旧值
          // 使用setTimeout确保store更新完成后再检查
          setTimeout(() => {
            const backendProjectIds = new Set(response.data.map(p => p.id))
            const currentStoreProjects = projects // 这里projects是闭包中的值，可能不是最新的
            const localOnlyProjects = currentStoreProjects.filter(localProject => !backendProjectIds.has(localProject.id))
            
            if (localOnlyProjects.length > 0) {
              console.warn(`⚠️ [Dashboard] 本地store中有 ${localOnlyProjects.length} 个项目不在后端列表中，将从store中移除:`, localOnlyProjects.map(p => ({ id: p.id, name: p.name })))
              // 从store中删除后端不存在的项目
              localOnlyProjects.forEach(project => {
                deleteProject(project.id)
              })
            }
          }, 100)
          
          console.log(`✅ [Dashboard] 同步完成`)
        }
      } catch (error) {
        console.error('❌ [Dashboard] 加载项目列表失败:', error)
        showError('加载项目列表失败，请刷新页面重试')
      } finally {
        setIsLoading(false)
      }
    }

    // v11.2.1: 加载 App 发布状态
    const loadAppStatus = async () => {
      try {
        // 只有管理员才能访问 v3 API
        if (user?.role !== 'admin' && user?.role !== 'super_admin') {
          return
        }
        
        const response = await ProjectPublishApiService.getProjectsWithAppStatus({ page: 1, page_size: 100 })
        if (response.success && response.data?.projects) {
          const statusMap = new Map<string, { app_id: string; app_name: string }[]>()
          response.data.projects.forEach(p => {
            if (p.published_apps && p.published_apps.length > 0) {
              statusMap.set(p.id, p.published_apps)
            }
          })
          setProjectAppStatus(statusMap)
          console.log(`✅ [Dashboard] 加载 App 发布状态完成，${statusMap.size} 个项目已发布到 App`)
        }
      } catch (error) {
        console.warn('⚠️ [Dashboard] 加载 App 发布状态失败（可能后端未实现 v3 API）:', error)
        // 不显示错误提示，因为这是可选功能
      }
    }

    loadProjects()
    loadAppStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]) // 只在组件挂载时加载一次

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`)
  }

  const handleCreateProject = async () => {
    if (!newProject.company.trim() || !newProject.name.trim()) {
      return
    }

    try {
      // 先调用后端API创建项目
      const response = await ProjectApiService.createProject({
      name: newProject.name,
      company: newProject.company,
      description: newProject.description || "暂无描述",
    })

      if (response.success && response.data) {
        // 创建成功后，需要获取完整的项目信息（因为ProjectCreateResponse不包含company等字段）
        // 先调用getProject获取完整信息
        const fullProjectResponse = await ProjectApiService.getProject(response.data.id)
        
        if (fullProjectResponse.success && fullProjectResponse.data) {
          const defaultNodes = getDefaultNodes()
          const defaultEdges = getDefaultEdges()
          
          // 立即保存默认的flow配置到后端，确保数据一致性
          try {
            console.log('💾 [Dashboard] 保存默认flow配置到后端:', {
              projectId: fullProjectResponse.data.id,
              nodesCount: defaultNodes.length,
              edgesCount: defaultEdges.length,
            })
            
            const flowSaveResponse = await FlowApiService.saveFlowConfig(
              fullProjectResponse.data.id,
              {
                nodes: defaultNodes,
                edges: defaultEdges,
                change_summary: '初始化默认流程配置',
              }
            )
            
            if (flowSaveResponse.success) {
              console.log('✅ [Dashboard] 默认flow配置保存成功')
            } else {
              console.warn('⚠️ [Dashboard] 默认flow配置保存失败，但项目已创建:', flowSaveResponse)
            }
          } catch (error: any) {
            // 保存flow配置失败不影响项目创建
            const status = error?.response?.status
            if (status === 409) {
              // 409 Conflict - 配置已存在，这是正常情况（可能用户快速切换页面）
              console.log('ℹ️ [Dashboard] Flow配置已存在，跳过保存')
            } else if (status === 401 || status === 403) {
              // 认证/授权错误
              console.error('❌ [Dashboard] 认证失败，无法保存flow配置')
              // 不显示错误提示，因为项目已创建成功，用户可以稍后手动保存
            } else if (status >= 500) {
              // 服务器错误
              console.error('❌ [Dashboard] 服务器错误，无法保存flow配置:', error)
              // 不显示错误提示，因为ProjectStudio打开时会自动保存
            } else {
              // 其他错误（网络错误、超时等）
              console.warn('⚠️ [Dashboard] 保存默认flow配置时出错:', error)
              // 不显示错误提示，因为ProjectStudio打开时会自动保存
            }
          }
          
          // 使用完整的项目信息添加到本地store
          addProject({
            ...fullProjectResponse.data,
            nodes: defaultNodes, // 新项目默认使用入口和出口节点
            edges: defaultEdges, // 新项目默认没有边
          })
        } else {
          // 如果获取完整信息失败，使用创建响应和请求参数补充
          addProject({
            ...response.data,
            company: newProject.company, // 使用请求参数中的company
            description: newProject.description || '',
            status: ProjectStatus.DRAFT,
            api_key_created_at: new Date().toISOString(),
            owner_id: '', // TODO: 从authStore获取
            current_version: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            nodes: getDefaultNodes(), // 使用默认节点
            edges: getDefaultEdges(), // 使用默认边
          })
        }

    setNewProject({ company: "", name: "", description: "" })
    setIsCreateDialogOpen(false)
        showSuccess('项目创建成功', '创建')
    
    // 创建后直接跳转到项目编辑页
        navigate(`/project/${response.data.id}`)
      } else {
        throw new Error('创建项目失败')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 创建项目失败:', error)
      showError('创建项目失败，请稍后重试')
    }
  }

  const handleDeleteProject = async () => {
    if (!projectToDelete) return

    try {
      // 先调用后端API删除项目
      const response = await ProjectApiService.deleteProject(projectToDelete.id)
      
      if (response.success) {
        // 删除成功后，从本地store移除
        deleteProject(projectToDelete.id)
        setProjectToDelete(null)
        setDeleteConfirmInput('')
        setIsDeleteDialogOpen(false)
        showSuccess('项目删除成功', '删除')
      } else {
        throw new Error('删除项目失败')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 删除项目失败:', error)
      showError('删除项目失败，请稍后重试')
    }
  }

  const handleDuplicateProject = async () => {
    if (!projectToDuplicate || !duplicateProjectData.name.trim()) return

    setIsDuplicating(true)
    try {
      const response = await ProjectApiService.duplicateProject(projectToDuplicate.id, {
        name: duplicateProjectData.name.trim(),
        include_members: duplicateProjectData.copyMembers,
      })
      
      if (response.success && response.data) {
        // 添加新项目到本地 store
        addProject(response.data)
        setProjectToDuplicate(null)
        setIsDuplicateDialogOpen(false)
        setDuplicateProjectData({ name: "", copyMembers: true })
        showSuccess(`项目已复制为 "${response.data.name}"`, '复制成功')
      } else {
        throw new Error('复制项目失败')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 复制项目失败:', error)
      showError('复制项目失败，请稍后重试')
    } finally {
      setIsDuplicating(false)
    }
  }

  const openProjectSettings = useCallback(async (project: AgentProject) => {
    setProjectToSettings(project)
    setIsSettingsDialogOpen(true)
    setIsLoadingSettings(true)
    
    try {
      const response = await ProjectApiService.getProjectSettings(project.id)
      if (response.success && response.data) {
        setProjectSettings({
          memory_enabled: response.data.memory_enabled ?? false,
          user_context_api: response.data.user_context_api ?? '',
          enabled_tool_ids: response.data.enabled_tool_ids ?? [],
        })
      } else {
        // 如果没有设置，使用默认值
        setProjectSettings({
          memory_enabled: false,
          user_context_api: '',
          enabled_tool_ids: [],
        })
      }
    } catch (error) {
      console.error('❌ [Dashboard] 加载项目设置失败:', error)
      setProjectSettings({
        memory_enabled: false,
        user_context_api: '',
        enabled_tool_ids: [],
      })
    } finally {
      setIsLoadingSettings(false)
    }
  }, [])
  
  const handleSaveSettings = useCallback(async () => {
    if (!projectToSettings) return
    
    setIsSavingSettings(true)
    try {
      const response = await ProjectApiService.updateProjectSettings(projectToSettings.id, projectSettings)
      if (response.success) {
        showSuccess('项目设置已保存')
        setIsSettingsDialogOpen(false)
        setProjectToSettings(null)
      } else {
        showError('保存设置失败: 未知错误')
      }
    } catch (error) {
      console.error('❌ [Dashboard] 保存项目设置失败:', error)
      showError('保存设置失败，请重试')
    } finally {
      setIsSavingSettings(false)
    }
  }, [projectToSettings, projectSettings, showSuccess, showError])

  // ==================== 成员管理相关函数 ====================
  
  // 加载成员列表
  const loadMembers = useCallback(async (projectId: string) => {
    setIsLoadingMembers(true)
    try {
      const response = await ProjectApiService.getProjectMembers(projectId)
      if (response.success && response.data) {
        setMembers(response.data)
      }
    } catch (error) {
      console.error('加载成员列表失败:', error)
      showError('加载成员列表失败')
    } finally {
      setIsLoadingMembers(false)
    }
  }, [showError])

  // 搜索系统用户
  const handleSearchUsers = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setSearchedUsers([])
      return
    }
    
    setIsSearchingUsers(true)
    try {
      const response = await authService.getUsers({ search: keyword, page_size: 10 })
      if (response.data) {
        // 过滤掉已经是成员的用户
        const memberIds = members.map(m => m.user_id)
        const filteredUsers = response.data.filter(u => !memberIds.includes(u.id))
        setSearchedUsers(filteredUsers)
      }
    } catch (error) {
      console.error('搜索用户失败:', error)
    } finally {
      setIsSearchingUsers(false)
    }
  }, [members])

  // 添加成员
  const handleAddMember = useCallback(async () => {
    if (!projectToManageMembers || !selectedUserToAdd) return
    
    setIsAddingMember(true)
    try {
      const response = await ProjectApiService.addProjectMember(projectToManageMembers.id, {
        email: selectedUserToAdd.email,
        permission: selectedPermission,
      })
      
      if (response.success) {
        showSuccess(`已添加成员 ${selectedUserToAdd.name || selectedUserToAdd.email}`)
        setSelectedUserToAdd(null)
        setMemberSearchKeyword('')
        setSearchedUsers([])
        setSelectedPermission('member')
        // 重新加载成员列表
        loadMembers(projectToManageMembers.id)
      }
    } catch (error) {
      console.error('添加成员失败:', error)
      showError('添加成员失败，用户可能不存在或已是成员')
    } finally {
      setIsAddingMember(false)
    }
  }, [projectToManageMembers, selectedUserToAdd, selectedPermission, showSuccess, showError, loadMembers])

  // 修改成员权限
  const handleUpdatePermission = useCallback(async (memberId: string, newPermission: 'admin' | 'member') => {
    if (!projectToManageMembers) return
    
    try {
      const response = await ProjectApiService.updateMemberPermission(
        projectToManageMembers.id,
        memberId,
        { permission: newPermission }
      )
      
      if (response.success) {
        showSuccess('权限已更新')
        loadMembers(projectToManageMembers.id)
      }
    } catch (error) {
      console.error('更新权限失败:', error)
      showError('更新权限失败')
    }
  }, [projectToManageMembers, showSuccess, showError, loadMembers])

  // 打开移除成员确认弹窗
  const handleRemoveMember = useCallback((memberId: string, memberName: string) => {
    setMemberToRemove({ id: memberId, name: memberName })
    setIsRemoveMemberDialogOpen(true)
  }, [])
  
  // 确认移除成员
  const confirmRemoveMember = useCallback(async () => {
    if (!projectToManageMembers || !memberToRemove) return
    
    setIsRemovingMember(true)
    try {
      const response = await ProjectApiService.deleteProjectMember(projectToManageMembers.id, memberToRemove.id)
      
      if (response.success) {
        showSuccess(`已移除成员 ${memberToRemove.name}`)
        loadMembers(projectToManageMembers.id)
        setIsRemoveMemberDialogOpen(false)
        setMemberToRemove(null)
      }
    } catch (error) {
      console.error('移除成员失败:', error)
      showError('移除成员失败')
    } finally {
      setIsRemovingMember(false)
    }
  }, [projectToManageMembers, memberToRemove, showSuccess, showError, loadMembers])

  // 转让所有权
  const handleTransferOwnership = useCallback(async () => {
    if (!projectToManageMembers || !transferTargetUser) return
    
    setIsTransferring(true)
    try {
      const response = await ProjectApiService.transferProject(projectToManageMembers.id, {
        new_owner_id: transferTargetUser.user_id,
      })
      
      if (response.success) {
        showSuccess(`已将项目所有权转让给 ${transferTargetUser.name}`)
        setIsTransferDialogOpen(false)
        setTransferTargetUser(null)
        loadMembers(projectToManageMembers.id)
      }
    } catch (error) {
      console.error('转让所有权失败:', error)
      showError('转让所有权失败')
    } finally {
      setIsTransferring(false)
    }
  }, [projectToManageMembers, transferTargetUser, showSuccess, showError, loadMembers])

  // 判断当前用户是否可以转让所有权
  const canTransferOwnership = useCallback(() => {
    if (!user || !projectToManageMembers) return false
    
    // 系统管理员可以转让
    if (user.role === 'admin' || user.role === 'super_admin') return true
    
    // 项目所有者可以转让
    const currentUserMember = members.find(m => m.user_id === user.id)
    return currentUserMember?.is_owner === true
  }, [user, projectToManageMembers, members])

  // 判断当前用户是否可以管理成员
  const canManageMembers = useCallback(() => {
    if (!user || !projectToManageMembers) return false
    
    // 系统管理员可以管理
    if (user.role === 'admin' || user.role === 'super_admin') return true
    
    // 项目所有者或管理员可以管理
    const currentUserMember = members.find(m => m.user_id === user.id)
    return currentUserMember?.permission === 'owner' || currentUserMember?.permission === 'admin'
  }, [user, projectToManageMembers, members])

  // 打开成员管理弹窗时加载成员
  useEffect(() => {
    if (isMemberDialogOpen && projectToManageMembers) {
      loadMembers(projectToManageMembers.id)
    }
  }, [isMemberDialogOpen, projectToManageMembers, loadMembers])

  // 搜索用户防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearchKeyword) {
        handleSearchUsers(memberSearchKeyword)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [memberSearchKeyword, handleSearchUsers])

  const getStatusBadge = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.ACTIVE:
        return <Badge variant="default" className="whitespace-nowrap">运行中</Badge>
      case ProjectStatus.MAINTENANCE:
        return <Badge variant="secondary" className="whitespace-nowrap">维护中</Badge>
      case ProjectStatus.DRAFT:
        return <Badge variant="outline" className="whitespace-nowrap">草稿</Badge>
    }
  }

  // ====== 搜索、筛选、分组 ======
  // 提取所有公司名称（去重、排序）
  const companyList = Array.from(new Set(projects.map(p => p.company).filter(Boolean))).sort()

  // 按搜索词和公司筛选后的项目
  const filteredProjects = projects.filter(p => {
    if (companyFilter !== 'all' && p.company !== companyFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return (
        p.name.toLowerCase().includes(q) ||
        (p.company || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
      )
    }
    return true
  })

  // 按公司分组（含未分类项目）
  const groupedProjects = (() => {
    if (!groupByCompany) return []
    const groups = companyList
      .filter(c => companyFilter === 'all' || c === companyFilter)
      .map(company => ({
        company,
        projects: filteredProjects.filter(p => p.company === company),
      }))
      .filter(g => g.projects.length > 0)
    // 未分类项目（company 为空）
    if (companyFilter === 'all') {
      const uncategorized = filteredProjects.filter(p => !p.company)
      if (uncategorized.length > 0) {
        groups.push({ company: '未分类', projects: uncategorized })
      }
    }
    return groups
  })()

  // 共享的项目卡片渲染函数，消除分组视图与平铺视图之间的重复代码
  const renderProjectCard = (project: AgentProject) => (
    <Card 
      key={project.id} 
      className="hover:shadow-lg transition-shadow cursor-pointer group h-full flex flex-col"
      onClick={() => handleProjectClick(project.id)}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {project.company || '未分类'}
            </div>
            <CardTitle className="text-xl group-hover:text-primary transition-colors">
              {project.name}
            </CardTitle>
            <CardDescription className="line-clamp-2 min-h-[40px]">
              {project.description}
            </CardDescription>
          </div>
          {getStatusBadge(project.status)}
        </div>
      </CardHeader>
      <CardContent className="pb-3 flex-1">
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-1">
            <User className="h-4 w-4" />
            <span>{getAgentCount(project.id)} Agents</span>
          </div>
          <div>更新于 {project.updated_at}</div>
        </div>
        {projectAppStatus.get(project.id) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {projectAppStatus.get(project.id)!.map(app => (
              <Badge key={app.app_id} variant="secondary" className="text-xs">
                <Package className="h-3 w-3 mr-1" />
                {app.app_name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="pt-0 mt-auto">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1" onClick={(e) => { e.stopPropagation(); handleProjectClick(project.id) }}>
              <Play className="h-3 w-3" /> 编辑
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => openProjectSettings(project)}>
                <Settings className="h-4 w-4 mr-2" />
                项目设置
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setProjectToEdit(project)
                setEditProject({
                  company: project.company || "",
                  name: project.name || "",
                  description: project.description || "",
                })
                setIsEditDialogOpen(true)
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                编辑信息
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setProjectToDuplicate(project)
                setDuplicateProjectData({ 
                  name: `${project.name} (副本)`, 
                  copyMembers: true 
                })
                setIsDuplicateDialogOpen(true)
              }}>
                <Copy className="h-4 w-4 mr-2" />
                复制项目
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setProjectToManageMembers(project)
                setIsMemberDialogOpen(true)
              }}>
                <Users className="h-4 w-4 mr-2" />
                成员管理
              </DropdownMenuItem>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}/devices`)}>
                  <Cpu className="h-4 w-4 mr-2" />
                  设备管理
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={() => handleExportProject(project)}
                disabled={isExporting === project.id}
              >
                {isExporting === project.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isExporting === project.id ? '导出中...' : '导出项目'}
              </DropdownMenuItem>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <DropdownMenuItem onClick={() => {
                  setProjectToPublishToApp(project)
                  setIsPublishToAppDialogOpen(true)
                }}>
                  <Package className="h-4 w-4 mr-2" />
                  发布到 App
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600 focus:text-red-600"
                onClick={() => {
                  setProjectToDelete(project)
                  setDeleteConfirmInput('')
                  setIsDeleteDialogOpen(true)
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                删除项目
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardFooter>
    </Card>
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* 顶部导航栏 */}
      <header className="border-b bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <span className="font-bold text-xl tracking-tight">Axiom</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/tools")}>
              工具管理
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/skills")} className="gap-2">
              <Zap className="h-4 w-4" />
              技能管理
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/identities")} className="gap-2">
              <Brain className="h-4 w-4" />
              身份引擎
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/memory")} className="gap-2">
              <Database className="h-4 w-4" />
              记忆测试
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/runtime/monitoring")} className="gap-2">
              <Activity className="h-4 w-4" />
              运行时
            </Button>
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'developer') && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/runtime/logs")} className="gap-2">
                <FileText className="h-4 w-4" />
                日志管理
              </Button>
            )}
            {/* App 管理入口 - 仅管理员可见 */}
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/apps")} className="gap-2">
                <Package className="h-4 w-4" />
                App 管理
              </Button>
            )}
            <Button variant="ghost" size="sm">文档</Button>
            <Button variant="ghost" size="sm">帮助</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback>
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 border-b">
                  <p className="text-sm font-medium">{user?.name || '用户'}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {/* 管理员入口 */}
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                      <Shield className="mr-2 h-4 w-4" />
                      用户管理
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={async () => {
                  try {
                    await logout()
                    navigate('/login')
                  } catch (error) {
                    console.error('退出登录失败:', error)
                    // 即使API调用失败，也清除本地状态并跳转
                    navigate('/login')
                  }
                }}>
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">项目列表</h1>
            <p className="text-slate-500 mt-1">管理和编排你的 AI Agent 项目。</p>
          </div>
          
          <div className="flex gap-2">
            {/* 隐藏的文件选择 input */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              onChange={handleImportProject}
              className="hidden"
            />
            <Button 
              variant="outline" 
              className="gap-2" 
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {isImporting ? '导入中...' : '导入项目'}
            </Button>
            <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" /> 新建项目
            </Button>
          </div>
        </div>

        {/* 搜索和筛选工具栏 */}
        {!isLoading && projects.length > 0 && (
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="搜索项目名称、公司、ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {companyList.length > 1 && (
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-48">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="全部公司" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部公司</SelectItem>
                  {companyList.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {companyList.length > 1 && (
              <Button
                variant={groupByCompany ? 'default' : 'outline'}
                size="sm"
                className="gap-2 whitespace-nowrap"
                onClick={() => setGroupByCompany(!groupByCompany)}
              >
                <Building2 className="h-4 w-4" />
                按公司分组
              </Button>
            )}
            {(searchQuery || companyFilter !== 'all') && (
              <span className="text-sm text-slate-500">
                {filteredProjects.length} / {projects.length} 个项目
              </span>
            )}
          </div>
        )}

        {/* 项目卡片网格 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-slate-500">加载项目列表中...</div>
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <FolderPlus className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">暂无项目</h3>
            <p className="text-slate-500 mb-6 max-w-sm">
              创建你的第一个 AI Agent 项目，开始构建智能对话流程。
            </p>
            <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" /> 创建第一个项目
            </Button>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-slate-500">没有找到匹配的项目</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => { setSearchQuery(''); setCompanyFilter('all') }}>
              清除筛选
            </Button>
          </div>
        ) : (() => {
          // 统一渲染：分组时按公司分段，不分组时作为一个整体
          const displayGroups = groupByCompany && groupedProjects.length > 0
            ? groupedProjects
            : [{ company: '', projects: filteredProjects }]

          const renderGrid = (items: AgentProject[], indent = false) => (
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${indent ? 'mt-3 pl-6' : ''}`}>
              {items.map((project) => renderProjectCard(project))}
              {/* 新建项目占位卡片 — 仅在最后一组显示 */}
              {items === displayGroups[displayGroups.length - 1].projects && (
                <Button 
                  variant="outline" 
                  className="h-full min-h-[200px] border-dashed flex flex-col gap-4 hover:border-primary hover:bg-primary/5"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                    <Plus className="h-6 w-6 text-slate-500" />
                  </div>
                  <span className="text-lg font-medium text-slate-600">创建新项目</span>
                </Button>
              )}
            </div>
          )

          return (
            <div className={groupByCompany ? 'space-y-6' : ''}>
              {displayGroups.map(group => 
                groupByCompany && group.company ? (
                  <Collapsible key={group.company} defaultOpen className="group">
                    <CollapsibleTrigger className="flex items-center gap-2 w-full text-left px-1 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                      <ChevronRight className="h-4 w-4 text-slate-400 transition-transform duration-200 group-data-[state=open]:rotate-90" />
                      <Building2 className="h-4 w-4 text-slate-500" />
                      <span className="font-semibold text-lg">{group.company}</span>
                      <Badge variant="secondary" className="text-xs">{group.projects.length}</Badge>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {renderGrid(group.projects, true)}
                    </CollapsibleContent>
                  </Collapsible>
                ) : (
                  renderGrid(group.projects)
                )
              )}
            </div>
          )
        })()}
      </main>

      {/* 创建项目弹窗 */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>创建新项目</DialogTitle>
            <DialogDescription>
              填写项目基本信息，创建后可以开始编排 Agent 流程。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="company">
                公司/团队名称 <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="company"
                  placeholder="例如：健康科技有限公司"
                  className="pl-10"
                  value={newProject.company}
                  onChange={(e) => setNewProject({ ...newProject, company: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">
                项目名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="例如：AI 跑步教练"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">项目描述（可选）</Label>
              <Textarea
                id="description"
                placeholder="简要描述这个项目的功能和目标..."
                className="resize-none"
                rows={3}
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={handleCreateProject}
              disabled={!newProject.company.trim() || !newProject.name.trim()}
            >
              创建项目
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 编辑信息弹窗 */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>编辑项目信息</DialogTitle>
            <DialogDescription>
              修改项目的基本信息。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-company">
                公司/团队名称 <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="edit-company"
                  placeholder="例如：健康科技有限公司"
                  className="pl-10"
                  value={editProject.company}
                  onChange={(e) => setEditProject({ ...editProject, company: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-name">
                项目名称 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-name"
                placeholder="例如：AI 跑步教练"
                value={editProject.name}
                onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-description">项目描述</Label>
              <Textarea
                id="edit-description"
                placeholder="简要描述这个项目的功能和目标..."
                className="resize-none"
                rows={3}
                value={editProject.description}
                onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isEditSaving}>
              取消
            </Button>
            <Button 
              onClick={handleEditProject}
              disabled={!editProject.company?.trim() || !editProject.name?.trim() || isEditSaving}
            >
              {isEditSaving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除项目确认弹窗（安全二次确认） */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => { setIsDeleteDialogOpen(open); if (!open) setDeleteConfirmInput('') }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              删除项目
            </DialogTitle>
            <DialogDescription>
              此操作不可撤销，项目及其所有配置将被永久删除。
            </DialogDescription>
          </DialogHeader>

          {projectToDelete && (
            <div className="space-y-4 py-2">
              {/* 项目信息展示 */}
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-600 dark:text-slate-400">公司：</span>
                  <span className="font-medium">{projectToDelete.company}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FolderPlus className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-600 dark:text-slate-400">项目：</span>
                  <span className="font-medium">{projectToDelete.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-600 dark:text-slate-400 ml-6">ID：</span>
                  <code className="bg-white dark:bg-slate-800 px-2 py-0.5 rounded text-xs font-mono border select-all">
                    {projectToDelete.id}
                  </code>
                </div>
              </div>

              {/* 输入确认 */}
              <div className="space-y-2">
                <Label className="text-sm">
                  请输入项目 ID <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{projectToDelete.id}</code> 以确认删除：
                </Label>
                <Input
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder="输入项目 ID..."
                  className="font-mono text-sm"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProject}
              disabled={!projectToDelete || deleteConfirmInput !== projectToDelete.id}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 移除成员确认弹窗 */}
      <Dialog 
        open={isRemoveMemberDialogOpen} 
        onOpenChange={(open) => {
          setIsRemoveMemberDialogOpen(open)
          if (!open) setMemberToRemove(null)
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>移除成员</DialogTitle>
            <DialogDescription>
              确定要将「{memberToRemove?.name || '该成员'}」从项目中移除吗？移除后该成员将无法访问此项目。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsRemoveMemberDialogOpen(false)
                setMemberToRemove(null)
              }}
              disabled={isRemovingMember}
            >
              取消
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmRemoveMember}
              disabled={isRemovingMember}
            >
              {isRemovingMember ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  移除中...
                </>
              ) : '移除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 复制项目弹窗 */}
      <Dialog open={isDuplicateDialogOpen} onOpenChange={(open) => {
        setIsDuplicateDialogOpen(open)
        if (!open) {
          setProjectToDuplicate(null)
          setDuplicateProjectData({ name: "", copyMembers: true })
        }
      }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>复制项目</DialogTitle>
            <DialogDescription>
              将 <strong>{projectToDuplicate?.name}</strong> 复制为新项目
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="duplicate-name">新项目名称</Label>
              <Input
                id="duplicate-name"
                value={duplicateProjectData.name}
                onChange={(e) => setDuplicateProjectData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="输入新项目名称"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="copy-members">复制项目成员</Label>
                <p className="text-sm text-muted-foreground">将原项目的成员添加到新项目中</p>
              </div>
              <Switch
                id="copy-members"
                checked={duplicateProjectData.copyMembers}
                onCheckedChange={(checked) => setDuplicateProjectData(prev => ({ ...prev, copyMembers: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicateDialogOpen(false)} disabled={isDuplicating}>
              取消
            </Button>
            <Button 
              onClick={handleDuplicateProject}
              disabled={!duplicateProjectData.name.trim() || isDuplicating}
            >
              {isDuplicating ? '复制中...' : '确认复制'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 项目设置弹窗 */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={(open) => {
        setIsSettingsDialogOpen(open)
        if (!open) {
          setProjectToSettings(null)
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              项目设置
            </DialogTitle>
            <DialogDescription>
              配置 <strong>{projectToSettings?.name}</strong> 的项目级别设置
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingSettings ? (
            <div className="py-8 text-center text-slate-500">
              加载设置中...
            </div>
          ) : (
            <div className="grid gap-6 py-4">
              {/* 用户上下文 API */}
              <div className="grid gap-3">
                <Label htmlFor="user-context-api" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  用户上下文 API
                </Label>
                <Input
                  id="user-context-api"
                  placeholder="https://your-api.com/user/profile?user_id={user_id}"
                  value={projectSettings.user_context_api || ''}
                  onChange={(e) => setProjectSettings(prev => ({
                    ...prev,
                    user_context_api: e.target.value
                  }))}
                />
                <p className="text-xs text-slate-500">
                  配置后，Agent 可选择启用自动获取用户画像并注入到系统提示词中。
                  <br />
                  支持 <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{'{user_id}'}</code> 占位符。
                </p>
              </div>

              {/* 分割线 */}
              <div className="border-t" />
              
              {/* 启用记忆 */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="memory-enabled" className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-slate-500" />
                    启用记忆
                  </Label>
                  <p className="text-xs text-slate-500">
                    启用后，Agent 将自动记录并利用用户的对话历史
                  </p>
                </div>
                <Switch
                  id="memory-enabled"
                  checked={projectSettings.memory_enabled}
                  onCheckedChange={(checked) => setProjectSettings(prev => ({
                    ...prev,
                    memory_enabled: checked
                  }))}
                />
              </div>

              {/* 分割线 */}
              <div className="border-t" />

              {/* 可用工具 */}
              <div className="grid gap-3">
                <Label className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-slate-500" />
                  可用工具
                </Label>
                <p className="text-xs text-slate-500 -mt-2">
                  选择该项目中 Agent 可以使用的工具
                </p>
                {isLoadingTools ? (
                  <div className="text-sm text-slate-500">加载工具列表...</div>
                ) : allTools.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    暂无可用工具，请先在
                    <Button variant="link" className="px-1 h-auto" onClick={() => navigate('/tools')}>
                      工具管理
                    </Button>
                    中创建
                  </div>
                ) : (
                  <div className="max-h-[280px] overflow-y-auto space-y-2 border rounded-lg p-2">
                    {(() => {
                      // 按 server_name 分组，按字母顺序排序
                      const grouped = allTools.reduce((acc, tool) => {
                        const serverName = tool.server_name || '未分类'
                        if (!acc[serverName]) acc[serverName] = []
                        acc[serverName].push(tool)
                        return acc
                      }, {} as Record<string, typeof allTools>)
                      
                      const sortedServerNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b))
                      
                      return sortedServerNames.map((serverName) => {
                        const serverTools = grouped[serverName]
                        const selectedCount = serverTools.filter(t => 
                          projectSettings.enabled_tool_ids?.includes(t.id)
                        ).length
                        const allSelected = selectedCount === serverTools.length
                        const someSelected = selectedCount > 0 && selectedCount < serverTools.length
                        
                        return (
                          <Collapsible key={serverName} className="border rounded-md group/server">
                            <CollapsibleTrigger className="flex w-full items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded-t-md">
                              <div className="flex items-center gap-2">
                                <ChevronRight className="h-3.5 w-3.5 text-slate-400 transition-transform duration-200 group-data-[state=open]/server:rotate-90" />
                                <span className="font-medium text-sm">{serverName}</span>
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                  {selectedCount}/{serverTools.length}
                                </Badge>
                              </div>
                              <div 
                                className="flex items-center gap-1.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                  onCheckedChange={(checked) => {
                                    setProjectSettings(prev => {
                                      const currentIds = prev.enabled_tool_ids ?? []
                                      const serverToolIds = serverTools.map(t => t.id)
                                      let newIds: string[]
                                      if (checked) {
                                        // 全选：添加该服务所有工具
                                        newIds = [...new Set([...currentIds, ...serverToolIds])]
                                      } else {
                                        // 取消全选：移除该服务所有工具
                                        newIds = currentIds.filter(id => !serverToolIds.includes(id))
                                      }
                                      return { ...prev, enabled_tool_ids: newIds }
                                    })
                                  }}
                                  className="h-3.5 w-3.5"
                                />
                                <span className="text-[10px] text-slate-400">全选</span>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="border-t">
                              <div className="px-3 py-2 space-y-1.5">
                                {serverTools.map((tool) => {
                                  const isChecked = projectSettings.enabled_tool_ids?.includes(tool.id) ?? false
                                  return (
                                    <div key={tool.id} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`tool-${tool.id}`}
                                        checked={isChecked}
                                        onCheckedChange={(checked) => {
                                          setProjectSettings(prev => {
                                            const currentIds = prev.enabled_tool_ids ?? []
                                            const newIds = checked
                                              ? [...currentIds, tool.id]
                                              : currentIds.filter(id => id !== tool.id)
                                            return { ...prev, enabled_tool_ids: newIds }
                                          })
                                        }}
                                        className="h-3.5 w-3.5"
                                      />
                                      <label
                                        htmlFor={`tool-${tool.id}`}
                                        className="text-xs cursor-pointer flex-1 text-slate-600 dark:text-slate-300"
                                      >
                                        {tool.name}
                                      </label>
                                    </div>
                                  )
                                })}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        )
                      })
                    })()}
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  已选择 {projectSettings.enabled_tool_ids?.length ?? 0} 个工具
                </p>
              </div>
              
              {/* 提示信息 */}
              {projectSettings.user_context_api && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    配置用户上下文 API 后，需要在 Agent 节点中单独开启"启用用户上下文"开关才会生效。
                  </span>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsSettingsDialogOpen(false)
                setProjectToSettings(null)
              }}
            >
              取消
            </Button>
            <Button 
              onClick={handleSaveSettings}
              disabled={isSavingSettings || isLoadingSettings}
            >
              {isSavingSettings ? '保存中...' : '保存设置'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 成员管理弹窗 */}
      <Dialog open={isMemberDialogOpen} onOpenChange={(open) => {
        setIsMemberDialogOpen(open)
        if (!open) {
          setProjectToManageMembers(null)
          setMembers([])
          setMemberSearchKeyword('')
          setSearchedUsers([])
          setSelectedUserToAdd(null)
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              成员管理
            </DialogTitle>
            <DialogDescription>
              管理 <strong>{projectToManageMembers?.name}</strong> 的项目成员
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* 添加成员区域 */}
            {canManageMembers() && (
              <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <UserPlus className="h-4 w-4" />
                  添加成员
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="搜索用户（姓名或邮箱）"
                      value={memberSearchKeyword}
                      onChange={(e) => setMemberSearchKeyword(e.target.value)}
                      className="pl-9"
                    />
                    {isSearchingUsers && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                    )}
                  </div>
                  <Select value={selectedPermission} onValueChange={(v: 'admin' | 'member') => setSelectedPermission(v)}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">管理员</SelectItem>
                      <SelectItem value="member">成员</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* 搜索结果 */}
                {searchedUsers.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-[150px] overflow-y-auto">
                    {searchedUsers.map((u) => (
                      <div
                        key={u.id}
                        className={`flex items-center justify-between p-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer ${
                          selectedUserToAdd?.id === u.id ? 'bg-primary/10' : ''
                        }`}
                        onClick={() => setSelectedUserToAdd(u)}
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={u.avatar} />
                            <AvatarFallback className="text-xs">
                              {u.name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{u.name || '未设置姓名'}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        {selectedUserToAdd?.id === u.id && (
                          <Badge variant="secondary" className="text-xs">已选择</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* 已选择的用户 */}
                {selectedUserToAdd && (
                  <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={selectedUserToAdd.avatar} />
                        <AvatarFallback>
                          {selectedUserToAdd.name?.charAt(0) || selectedUserToAdd.email.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{selectedUserToAdd.name || selectedUserToAdd.email}</p>
                        <p className="text-xs text-slate-500">将添加为{selectedPermission === 'admin' ? '管理员' : '成员'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUserToAdd(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                      <Button size="sm" onClick={handleAddMember} disabled={isAddingMember}>
                        {isAddingMember ? <Loader2 className="h-4 w-4 animate-spin" /> : '添加'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* 成员列表 */}
            <div className="flex-1 overflow-y-auto">
              <Label className="text-sm font-medium mb-2 block">
                当前成员 ({members.length})
              </Label>
              {isLoadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  暂无成员
                </div>
              ) : (
                <div className="border rounded-lg divide-y">
                  {members.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={member.avatar} />
                          <AvatarFallback>
                            {member.name?.charAt(0) || member.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{member.name || '未设置姓名'}</p>
                            {member.is_owner && (
                              <Crown className="h-4 w-4 text-amber-500" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.is_owner ? (
                          <Badge variant="default" className="bg-amber-500">所有者</Badge>
                        ) : canManageMembers() ? (
                          <>
                            <Select
                              value={member.permission}
                              onValueChange={(v: 'admin' | 'member') => handleUpdatePermission(member.user_id, v)}
                            >
                              <SelectTrigger className="w-[100px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">管理员</SelectItem>
                                <SelectItem value="member">成员</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-red-600"
                              onClick={() => handleRemoveMember(member.user_id, member.name)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Badge variant="outline">
                            {member.permission === 'admin' ? '管理员' : '成员'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            {canTransferOwnership() && (
              <Button
                variant="outline"
                className="mr-auto"
                onClick={() => setIsTransferDialogOpen(true)}
              >
                转让所有权
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsMemberDialogOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 转让所有权确认弹窗 */}
      <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Crown className="h-5 w-5" />
              转让项目所有权
            </DialogTitle>
            <DialogDescription>
              将 <strong>{projectToManageMembers?.name}</strong> 的所有权转让给其他成员。转让后您将变为管理员。
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Label>选择新的所有者</Label>
            <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
              {members.filter(m => !m.is_owner).map((member) => (
                <div
                  key={member.user_id}
                  className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${
                    transferTargetUser?.user_id === member.user_id ? 'bg-primary/10' : ''
                  }`}
                  onClick={() => setTransferTargetUser(member)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={member.avatar} />
                      <AvatarFallback>
                        {member.name?.charAt(0) || member.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{member.name || '未设置姓名'}</p>
                      <p className="text-xs text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  {transferTargetUser?.user_id === member.user_id && (
                    <Badge variant="secondary">已选择</Badge>
                  )}
                </div>
              ))}
              {members.filter(m => !m.is_owner).length === 0 && (
                <div className="p-4 text-center text-slate-500 text-sm">
                  没有可转让的成员，请先添加成员
                </div>
              )}
            </div>
            
            {transferTargetUser && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ 确认将所有权转让给 <strong>{transferTargetUser.name}</strong>？此操作不可撤销。
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsTransferDialogOpen(false)
              setTransferTargetUser(null)
            }}>
              取消
            </Button>
            <Button
              variant="default"
              className="bg-amber-500 hover:bg-amber-600"
              disabled={!transferTargetUser || isTransferring}
              onClick={handleTransferOwnership}
            >
              {isTransferring ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              确认转让
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v11.2.1: 发布到 App 弹窗 */}
      {projectToPublishToApp && (
        <QuickPublishToAppDialog
          open={isPublishToAppDialogOpen}
          onOpenChange={(open) => {
            setIsPublishToAppDialogOpen(open)
            if (!open) {
              setProjectToPublishToApp(null)
            }
          }}
          projectId={projectToPublishToApp.id}
          projectName={projectToPublishToApp.name}
          projectDescription={projectToPublishToApp.description}
          onSuccess={async () => {
            // 刷新 App 发布状态
            try {
              const response = await ProjectPublishApiService.getProjectsWithAppStatus({ page: 1, page_size: 100 })
              if (response.success && response.data?.projects) {
                const statusMap = new Map<string, { app_id: string; app_name: string }[]>()
                response.data.projects.forEach(p => {
                  if (p.published_apps && p.published_apps.length > 0) {
                    statusMap.set(p.id, p.published_apps)
                  }
                })
                setProjectAppStatus(statusMap)
              }
            } catch (error) {
              console.warn('⚠️ [Dashboard] 刷新 App 发布状态失败:', error)
            }
          }}
        />
      )}
    </div>
  )
}

